import { useTranslation } from 'react-i18next'
import { confirmDialog } from '../../../components/ui/ConfirmDialog'
import { useToast } from '../../../components/ui/ToastProvider'
import { GRANTS } from '../../../auth/grants'
import { useGrants } from '../../../auth/useCurrentUser'
import { subActionDeleteErrorKey } from '../statusError'
import type { ActionListItem, ActionScope } from '../types'
import { useDeleteAction, useDeleteListSubAction } from './useActions'

/** Onay penceresi grubu — panelin `<ConfirmDialog group=…>` örneğiyle eşleşir. */
export const ACTION_LIST_DELETE_GROUP = 'action-list-delete'

/**
 * **Sil düğmesinin gösterildiği kapsamlar** (kullanıcı isteği 2026-09-17: *"tüm görevler ve
 * oluşturduklarım sayfasındaki tablolara silme butonu ekleyelim"*). Diğer alt sayfalar bilinçle
 * dışarıdadır: **Bana Atananlar**, **Takip Ettiklerim** (salt-okuma bir ilişki) ve **Delegasyon**
 * (Anasayfa kartının hedefi) — kullanıcı silmeyi bu iki ekran için istedi.
 *
 * <p><b>Bu liste TEK kapıdır, sunucu ikinci bir ağ örmez:</b> `deletable` bayrağı kapsama bakmaz
 * (alt görev satırlarına da `true` gelir — 2026-09-17'den beri). Yani buraya bir kapsam eklemek,
 * o ekranda silmeyi gerçekten açar; "nasılsa sunucu engeller" diye düşünülmemeli.
 */
const DELETE_SCOPES: readonly ActionScope[] = ['all', 'created']

/**
 * Satırdaki çöp kutusunun etiket anahtarı — {@code deleteLabel} ile birlikte kullanılır ("{ad} görevini
 * sil" / "{ad} alt görevini sil"). Alt görevde AYRI metin: erişilebilir ad, ekran okuyucu ve sesli
 * kontrol kullanıcısının satırı ayırt etmesinin tek yoludur; türü yanlış söylemek onu yanıltır.
 * Tablo ve kart aynı yerden okusun diye burada durur.
 */
export function deleteLabelKey(row: Pick<ActionListItem, 'subAction'>): string {
  return row.subAction ? 'actions.detail.subActions.delete' : 'actions.detail.delete.action'
}

/**
 * Görev listesindeki (tablo + dar ekran kartı) <b>silme</b> davranışı — kullanıcı isteği 2026-09-17.
 *
 * <p><b>Neden ayrı bir hook:</b> {@code ActionsListPanel} zaten uygulamanın en dallı bileşenidir;
 * onay + mutasyon + geri bildirim gövdeye yazılsaydı bilişsel karmaşıklığı (Sonar S3776, eşik 15)
 * boş yere artardı. Aynı desen: {@code useMeetingRecordDelete} (Toplantı Detay).
 *
 * <p><b>Silme kapsamlıdır, kaydın kökenine bakmaz:</b> toplantıdan doğan görev de, toplantısı olmayan
 * <b>bağımsız</b> görev de aynı uçtan silinir ({@code DELETE /actions/{id}}) — sunucu bağımsız görevde
 * de denetim izi yazar ({@code MEETING_ID} null, bağlam {@code MEETING_MINUTES_ID}).
 *
 * <p><b>"Silme bu ekranda mümkün mü?" kararı da burada</b> — çağıran yalnız nerede olduğunu söyler.
 * Üç koşul birlikte aranır: <b>yetki</b> ({@link GRANTS.RECORD_DELETE} — görev, alt görev, gündem ve
 * prensip silmeyi kapsayan tek yetki; uygulamadaki diğer silme düğmeleriyle aynı kapı), <b>kapsam</b>
 * ({@link DELETE_SCOPES}) ve <b>arşiv değil</b> (arşivlenen görev sonlanmıştır). Karar çağıranın
 * gövdesinde yazılıydı ve panelin bilişsel karmaşıklığını ölçülen 14'ten 15'e çıkarıyordu (Sonar eşiği
 * tam 15; ölçüm `eslint-plugin-sonarjs` ile) — kuralın burada durması hem ölçümü hem okunabilirliği
 * düzeltir: silmenin tüm kuralları tek dosyada.
 *
 * <p>Yetki <b>kolon düzeyinde</b> de sorulur (satır başına gelen `deletable` alanı varken): yetkisiz
 * kullanıcı boş bir kolon görmemeli. Satır başına ikinci kapı çağıranda `row.deletable`dır.
 *
 * @param scope bulunulan alt sayfa ({@link DELETE_SCOPES} ile karşılaştırılır)
 * @param archived arşiv listesi mi
 * @return onay + silme geri çağrısı; koşullar sağlanmıyorsa `undefined` → tablo/kart çöp kutusunu
 *     hiç çizmez
 */
export function useActionListDelete(scope: ActionScope, archived: boolean) {
  const { t } = useTranslation()
  const toast = useToast()
  const removeRoot = useDeleteAction()
  const removeSub = useDeleteListSubAction()
  const { hasGrant } = useGrants()
  const enabled = hasGrant(GRANTS.RECORD_DELETE) && !archived && DELETE_SCOPES.includes(scope)

  /**
   * Satır <b>alt görev</b> mi, kök görev mi? — ikisi AYRI uçtan silinir ve metinleri de ayrıdır.
   * Ölçüt sunucunun gönderdiği {@code subAction} alanıdır; "alt görev de silinebilir" kararı
   * 2026-09-17'de verildi (öncesinde sunucu alt satırlara {@code deletable=false} gönderiyordu).
   */
  const subActionTexts = {
    header: 'actions.detail.subActions.delete',
    // Kök metni "…ve varsa alt görevleri silinecek" der; alt görevin altında görev olmadığı için
    // orada yanlış olurdu.
    confirm: 'actions.detail.subActions.deleteConfirmNamed',
    done: 'actions.detail.subActions.deleted',
  }
  const rootTexts = {
    header: 'actions.detail.delete.action',
    confirm: 'actions.detail.delete.confirmNamed',
    done: 'actions.detail.delete.done',
  }

  /**
   * Onay metni görevi <b>adıyla</b> sorar: listede yirmi satır vardır ve yanlış çöp kutusuna basmak
   * geri alınamaz. Kök görevde kademeli alt görev silmesi de metinde söylenir (`confirmNamed`).
   */
  const confirmDelete = (row: ActionListItem) => {
    const texts = row.subAction ? subActionTexts : rootTexts
    return confirmDialog({
      group: ACTION_LIST_DELETE_GROUP,
      header: t(texts.header),
      message: t(texts.confirm, { name: row.name || '—' }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      // Liste tazeleme mutasyonların kendi `onSuccess`'lerindedir (aksiyon + arşiv + sorumlu
      // seçenekleri, alt görevde ayrıca üst detay); burada tekrarlanmaz ki iki yer ayrışmasın.
      accept: () =>
        (row.subAction ? removeSub : removeRoot).mutate(row.id, {
          onSuccess: () => toast.success(t(texts.done)),
          // Alt görev silme, üst görevi sorumlusuz bırakacaksa 400 döner ve bu nadir değildir —
          // nedeni söyleyen metin `subActionDeleteErrorKey`de (merkezî toast `handledCodes` ile
          // susturuldu, bkz. `useDeleteListSubAction`).
          onError: (err) =>
            toast.error(
              row.subAction ? t(subActionDeleteErrorKey(err)) : t('actions.detail.delete.error'),
            ),
        }),
    })
  }

  return enabled ? confirmDelete : undefined
}
